import { api, type QueryParams } from '@/lib/api/client';
import type {
  ContactDirectoryEntry,
  Country,
  I18n,
  NavItem,
  PageCopyNamespace,
  PageCopyUsages,
  Paginated,
  Redirect,
  SettingsDocument,
  UiString,
} from '@/lib/api/types';

/** A single page-copy namespace summary as returned by the list endpoint (spec §15.2). */
export interface PageCopySummary {
  namespace: string;
  ar_complete: boolean;
  updated_at: string;
  /** How many pages consume this namespace (reverse index). `0` ⇒ orphaned/unused. */
  used_by_count?: number;
}

/** Body shape for creating/updating a navigation item (label.en required). */
export interface NavItemBody {
  parent_id?: string | null;
  label: I18n;
  link_type: 'internal' | 'external';
  target_slug: string;
  url: string;
  open_in_new_tab: boolean;
  sort_order: number;
  is_visible: boolean;
}

export interface RedirectBody {
  from_path: string;
  to_path: string;
  status: 301 | 302 | 307 | 308;
  is_active?: boolean;
}

export interface ContactBody {
  label: I18n;
  email: string;
  phone?: string;
  sla?: string;
  is_visible: boolean;
  sort_order: number;
}

export interface CountryBody {
  is_default?: boolean;
  sort_order?: number;
}

/**
 * Body for `PATCH /admin/page-copy/{namespace}` — RFC 7396 JSON Merge Patch (docs
 * management.md §"merge semantics"). Send only what changes: nested objects merge key by
 * key, a JSON `null` deletes a key, scalars/arrays replace wholesale. Provide at least one
 * of `content` / `ar_complete`. PATCH never creates a namespace (404 if missing) — the admin
 * console only edits the copy of namespaces the site's authors have already defined.
 */
export interface PageCopyPatchBody {
  content?: Record<string, unknown> | null;
  ar_complete?: boolean;
}

/**
 * Single client for the whole Site Settings & Chrome surface (spec §15.2, docs management.md).
 * Every write is gated server-side by `settings:manage`, audited, and triggers an ISR
 * revalidation of the public layout shells.
 */
export const settingsApi = {
  // ── Settings document (field-level read-modify-write) ──────────────────────
  getSettings(): Promise<SettingsDocument> {
    return api.get<SettingsDocument>('/admin/settings');
  },
  // Accepts a PARTIAL document: the backend merges it onto the current singleton,
  // so callers send only what changed. Returns the full, updated document.
  putSettings(changes: Partial<SettingsDocument>): Promise<SettingsDocument> {
    return api.put<SettingsDocument>('/admin/settings', changes);
  },

  // ── UI strings ─────────────────────────────────────────────────────────────
  listStrings(): Promise<UiString[]> {
    return api.get<UiString[]>('/admin/strings');
  },
  putString(namespace: string, key: string, value: I18n): Promise<UiString> {
    return api.put<UiString>(
      `/admin/strings/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
      { value },
    );
  },
  deleteString(namespace: string, key: string): Promise<null> {
    return api.del(
      `/admin/strings/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
    );
  },

  // ── Navigation ───────────────────────────────────────────────────────────────
  getNavigation(key: string): Promise<NavItem[]> {
    return api.get<NavItem[]>(`/admin/navigation/${encodeURIComponent(key)}`);
  },
  addNavItem(key: string, body: NavItemBody): Promise<NavItem> {
    return api.post<NavItem>(`/admin/navigation/${encodeURIComponent(key)}/items`, body);
  },
  updateNavItem(id: string, body: Partial<NavItemBody>): Promise<NavItem> {
    return api.patch<NavItem>(`/admin/navigation/items/${id}`, body);
  },
  deleteNavItem(id: string): Promise<null> {
    return api.del(`/admin/navigation/items/${id}`);
  },
  reorderNav(key: string, ids: string[]): Promise<NavItem[]> {
    return api.post<NavItem[]>(`/admin/navigation/${encodeURIComponent(key)}/reorder`, { ids });
  },

  // ── Redirects ────────────────────────────────────────────────────────────────
  async listRedirects(params?: QueryParams): Promise<Paginated<Redirect>> {
    const res = await api.getRaw<Redirect[]>('/admin/redirects', params);
    return { data: res.data ?? [], meta: res.meta as Paginated<Redirect>['meta'] };
  },
  upsertRedirect(body: RedirectBody): Promise<Redirect> {
    return api.post<Redirect>('/admin/redirects', body);
  },
  deleteRedirect(id: string): Promise<null> {
    return api.del(`/admin/redirects/${id}`);
  },

  // ── Contact directory ──────────────────────────────────────────────────────
  listContactDirectory(): Promise<ContactDirectoryEntry[]> {
    return api.get<ContactDirectoryEntry[]>('/admin/contact-directory');
  },
  putContact(key: string, body: ContactBody): Promise<ContactDirectoryEntry> {
    return api.put<ContactDirectoryEntry>(
      `/admin/contact-directory/${encodeURIComponent(key)}`,
      body,
    );
  },
  deleteContact(key: string): Promise<null> {
    return api.del(`/admin/contact-directory/${encodeURIComponent(key)}`);
  },

  // ── Countries register ───────────────────────────────────────────────────────
  listCountries(): Promise<Country[]> {
    return api.get<Country[]>('/admin/countries');
  },
  putCountry(code: string, body: CountryBody): Promise<Country> {
    return api.put<Country>(`/admin/countries/${encodeURIComponent(code)}`, body);
  },
  setDefaultCountry(code: string): Promise<Country> {
    return api.post<Country>(`/admin/countries/${encodeURIComponent(code)}/default`);
  },
  deleteCountry(code: string): Promise<null> {
    return api.del(`/admin/countries/${encodeURIComponent(code)}`);
  },

  // ── Page copy ────────────────────────────────────────────────────────────────
  listPageCopy(): Promise<PageCopySummary[]> {
    return api.get<PageCopySummary[]>('/admin/page-copy');
  },
  getPageCopy(namespace: string): Promise<PageCopyNamespace> {
    return api.get<PageCopyNamespace>(`/admin/page-copy/${encodeURIComponent(namespace)}`);
  },
  /** Pages that consume this namespace (reverse index). 404 if the namespace is unknown. */
  pageCopyUsages(namespace: string): Promise<PageCopyUsages> {
    return api.get<PageCopyUsages>(`/admin/page-copy/${encodeURIComponent(namespace)}/usages`);
  },
  patchPageCopy(namespace: string, body: PageCopyPatchBody): Promise<PageCopyNamespace> {
    return api.patch<PageCopyNamespace>(
      `/admin/page-copy/${encodeURIComponent(namespace)}`,
      body,
    );
  },
};
